import React from 'react';
import { Building2, Satellite, Sparkles } from 'lucide-react';

export type WorkspaceMode = 'office' | 'space';

interface WorkspaceSwitcherProps {
  currentWorkspace: WorkspaceMode;
  onSwitchWorkspace: (mode: WorkspaceMode) => void;
  language?: string;
  className?: string;
  enterpriseName?: string;
}

export const WorkspaceSwitcher: React.FC<WorkspaceSwitcherProps> = ({
  currentWorkspace,
  onSwitchWorkspace,
  language = 'uk',
  className = '',
  enterpriseName
}) => {
  const isOffice = currentWorkspace === 'office';
  const isSpace = currentWorkspace === 'space';

  const officeTitle = language === 'pl'
    ? 'Cyfrowe Biuro'
    : language === 'en'
    ? 'Digital Office'
    : enterpriseName
    ? `Офіс: ${enterpriseName}`
    : 'Цифровий Офіс';

  const spaceTitle = language === 'pl'
    ? 'Hub Kosmiczno-Obronny'
    : language === 'en'
    ? 'Space & Defense Hub'
    : 'Космічно-Оборонний Хаб';

  return (
    <div
      role="group"
      aria-label="Перемикач робочого простору"
      className={`inline-flex items-center p-1 bg-slate-900/90 backdrop-blur-md rounded-xl border border-slate-700/80 shadow-inner gap-1 transition-all ${className}`}
    >
      {/* 🏛️ КОРПОРАТИВНИЙ ЦИФРОВИЙ ОФІС */}
      <button
        type="button"
        role="button"
        aria-pressed={isOffice}
        onClick={() => onSwitchWorkspace('office')}
        className={`relative flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3.5 py-1.5 rounded-lg text-xs font-mono font-bold transition-all duration-200 cursor-pointer select-none ${
          isOffice
            ? 'bg-gradient-to-r from-violet-600 via-indigo-600 to-purple-600 text-white shadow-md shadow-violet-500/30 border border-violet-400/40 ring-1 ring-white/20'
            : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/60 border border-transparent'
        }`}
        title="Перейти у Корпоративний Цифровий Офіс (Казначейство, ДПС, ПК Фіндокументи, договори, To-Do)"
      >
        <Building2 className={`w-4 h-4 ${isOffice ? 'text-amber-300 animate-pulse' : 'text-slate-400'}`} />
        <span className="tracking-tight whitespace-nowrap">{officeTitle}</span>
        <span
          className={`text-[9px] px-1.5 py-0.5 rounded-md font-mono font-black uppercase transition-colors shrink-0 ${
            isOffice
              ? 'bg-amber-400 text-slate-950 shadow-xs'
              : 'bg-slate-800 text-slate-400 border border-slate-700'
          }`}
        >
          B2B SaaS
        </span>
      </button>

      {/* 🛰️ КОСМІЧНО-ОБОРОННИЙ ХАБ */}
      <button
        type="button"
        role="button"
        aria-pressed={isSpace}
        onClick={() => onSwitchWorkspace('space')}
        className={`relative flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3.5 py-1.5 rounded-lg text-xs font-mono font-bold transition-all duration-200 cursor-pointer select-none ${
          isSpace
            ? 'bg-gradient-to-r from-cyan-600 via-blue-600 to-indigo-700 text-white shadow-md shadow-cyan-500/30 border border-cyan-400/40 ring-1 ring-white/20'
            : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/60 border border-transparent'
        }`}
        title="Перейти у Геоінформаційний Космічно-Оборонний Хаб (Sentinel-2, InSAR радар, Інфразвук ГЦСК/ППО, Кодифікатор Brave1)"
      >
        <Satellite className={`w-4 h-4 ${isSpace ? 'text-cyan-300 animate-pulse' : 'text-slate-400'}`} />
        <span className="tracking-tight whitespace-nowrap">{spaceTitle}</span>
        <span
          className={`text-[9px] px-1.5 py-0.5 rounded-md font-mono font-black uppercase transition-colors shrink-0 ${
            isSpace
              ? 'bg-cyan-400 text-slate-950 shadow-xs'
              : 'bg-slate-800 text-slate-400 border border-slate-700'
          }`}
        >
          GEOINT & AI
        </span>
      </button>
    </div>
  );
};

export default WorkspaceSwitcher;
